import React from 'react';

/** Icon component matching @nykaa/ui-components signature with styled-components support */
export type IconComponent = React.ComponentType<{
  size?: string;
  color?: string;
  title?: string;
  children?: React.ReactNode;
  viewBox?: string;
  className?: string;
  [key: string]: any;
}>;

/** Icon names organized by category */
export enum IconName {
  // Navigation & Branding
  HOME = 'HOME',
  LOGO = 'LOGO',
  HAMBURGER_MENU = 'HAMBURGER_MENU',
  CATEGORIES = 'CATEGORIES',
  SALE = 'SALE',
  ACCOUNT = 'ACCOUNT',
  COUPONS_OFFERS = 'COUPONS_OFFERS',
  HNM = 'HNM',
  GLOBAL_LUXE = 'GLOBAL_LUXE',
  IND_LUXE = 'IND_LUXE',
  ATELIER_LOGO = 'ATELIER_LOGO',

  // Arrows & Chevrons
  ARROW_LEFT = 'ARROW_LEFT',
  ARROW_RIGHT = 'ARROW_RIGHT',
  BACK = 'BACK',  // Alias for ARROW_LEFT for backwards compatibility
  CHEVRON_LEFT = 'CHEVRON_LEFT',
  CHEVRON_RIGHT = 'CHEVRON_RIGHT',
  CHEVRON_UP = 'CHEVRON_UP',
  CHEVRON_DOWN = 'CHEVRON_DOWN',
  CHEVRON_UP_SMALL = 'CHEVRON_UP_SMALL',
  CHEVRON_DOWN_SMALL = 'CHEVRON_DOWN_SMALL',
  CHEVRON_RIGHT_SMALL = 'CHEVRON_RIGHT_SMALL',

  // Close & Remove Actions
  CLOSE = 'CLOSE',
  CIRCLE_X_DUOTONE = 'CIRCLE_X_DUOTONE',
  CIRCLE_X_FILLED = 'CIRCLE_X_FILLED',
  TRASH_CAN = 'TRASH_CAN',

  // Basic Actions
  SEARCH = 'SEARCH',
  SEARCH_RECENT = 'SEARCH_RECENT',
  FILTER = 'FILTER',
  SORT = 'SORT',
  EDIT = 'EDIT',
  EDIT_1 = 'EDIT_1',
  COPY = 'COPY',
  SHARE_ANDROID = 'SHARE_ANDROID',
  LINK = 'LINK',
  RESET = 'RESET',
  PLUS = 'PLUS',
  MINUS = 'MINUS',

  // Status & Alerts
  ALERT_POSITIVE = 'ALERT_POSITIVE',
  ALERT_POSITIVE_FILLED = 'ALERT_POSITIVE_FILLED',
  ALERT_NEGATIVE = 'ALERT_NEGATIVE',
  ALERT_NEGATIVE_FILLED = 'ALERT_NEGATIVE_FILLED',
  ALERT_WARNING = 'ALERT_WARNING',
  ALERT_WARNING_FILLED = 'ALERT_WARNING_FILLED',
  ALERT_INFO = 'ALERT_INFO',
  ALERT_INFO_FILLED = 'ALERT_INFO_FILLED',
  NOTIFICATION_BELL = 'NOTIFICATION_BELL',
  CIRCLE_TICK = 'CIRCLE_TICK',

  // Ratings & Reviews
  STAR = 'STAR',
  STAR_FILLED = 'STAR_FILLED',
  STAR_HALF_FILLED = 'STAR_HALF_FILLED',
  THUMBS_UP = 'THUMBS_UP',
  THUMBS_UP_FILLED = 'THUMBS_UP_FILLED',

  // Shopping & Wishlist
  CART = 'CART',
  WISHLIST = 'WISHLIST',
  HEART_PLUS = 'HEART_PLUS',
  SIMILAR_ITEMS = 'SIMILAR_ITEMS',
  CIRCLE_EXCHANGE = 'CIRCLE_EXCHANGE',

  // Delivery & Orders
  DELIVERY_1 = 'DELIVERY_1',
  DELIVERY_2 = 'DELIVERY_2',
  BOX = 'BOX',
  SPECIAL_OFFERS = 'SPECIAL_OFFERS',
  OFFER_FILLED = 'OFFER_FILLED',

  // Gifts & Rewards
  GIFT_CARD = 'GIFT_CARD',
  GIFT_BOX_2 = 'GIFT_BOX_2',

  // User & Account
  USER = 'USER',
  USER_FILLED = 'USER_FILLED',
  NYKAA_N_HEART = 'NYKAA_N_HEART',

  // Location
  LOCATION_MARKER_FILLED = 'LOCATION_MARKER_FILLED',
  LOCATION_PIN = 'LOCATION_PIN',

  // Media & Upload
  CAMERA = 'CAMERA',
  IMAGE = 'IMAGE',
  EYE = 'EYE',
  CIRCLE_PLAY_FILLED = 'CIRCLE_PLAY_FILLED',
  TREND_LINE = 'TREND_LINE',
  FLIP_CAMERA = 'FLIP_CAMERA',
  DRAG_N_DROP = 'DRAG_N_DROP',

  // System UI
  CLOCK = 'CLOCK',
  MOBILE = 'MOBILE',
  HELP = 'HELP',
  CHAT = 'CHAT',
  CHAT_BUBBLE = 'CHAT_BUBBLE',
  CHAT_BUBBLE_V2 = 'CHAT_BUBBLE_V2',
  REDIRECT_ARROW = 'REDIRECT_ARROW',
  DOCUMENT = 'DOCUMENT',
  PRIVACY_POLICY = 'PRIVACY_POLICY',
  ADDRESS_BOOK = 'ADDRESS_BOOK',
  GLOBE = 'GLOBE',
  LEAVES = 'LEAVES',
  CARD_DEBIT_CREDIT = 'CARD_DEBIT_CREDIT',
  CIRCLE_LOCK = 'CIRCLE_LOCK',

  // Other
  DROPDOWN_FILLED = 'DROPDOWN_FILLED',
  CIRCLE_ARROW_UP_FILLED = 'CIRCLE_ARROW_UP_FILLED',
  CIRCLE_PLUS_FILLED = 'CIRCLE_PLUS_FILLED',

  // Additional Icons from shared/svg
  AI_STAR = 'AI_STAR',
  AUTHENTICATION_ICON = 'AUTHENTICATION_ICON',
  BELL = 'BELL',
  BIN = 'BIN',
  BORDER_HALF_CIRCLES = 'BORDER_HALF_CIRCLES',
  CAMERA_SVG = 'CAMERA_SVG',
  CARD = 'CARD',
  CHECK_MINUS = 'CHECK_MINUS',
  CIRCLE_ARROW = 'CIRCLE_ARROW',
  CIRCLE_EXCLAMATION = 'CIRCLE_EXCLAMATION',
  CIRCLE_INFO = 'CIRCLE_INFO',
  COPY_SVG = 'COPY_SVG',
  COUPON_ICON_SUCCESS = 'COUPON_ICON_SUCCESS',
  COUPON_LOCK = 'COUPON_LOCK',
  CROSS = 'CROSS',
  CROSS_ICON = 'CROSS_ICON',
  CROSS_HAIR = 'CROSS_HAIR',
  DISCOUNT_TAG = 'DISCOUNT_TAG',
  EASY_RETURNS = 'EASY_RETURNS',
  GUEST_PROFILE = 'GUEST_PROFILE',
  HELP_CENTER = 'HELP_CENTER',
  LOCATION = 'LOCATION',
  LOCK = 'LOCK',
  NYKAA_LOGO = 'NYKAA_LOGO',
  PENCIL = 'PENCIL',
  PLUS_SVG = 'PLUS_SVG',
  PROFILE = 'PROFILE',
  RATED_STAR = 'RATED_STAR',
  REFRESH = 'REFRESH',
  RELOAD = 'RELOAD',
  SECURE_PAYMENT = 'SECURE_PAYMENT',
  SHIPPING_N_RETURNS = 'SHIPPING_N_RETURNS',
  SQUARE_EXCLAMATION = 'SQUARE_EXCLAMATION',
  THREE_DOTS = 'THREE_DOTS',
  THUMBS_UP_SVG = 'THUMBS_UP_SVG',
  TICK = 'TICK',
  TICK_MARK = 'TICK_MARK',
  TICK_ROUNDED = 'TICK_ROUNDED',
  TRIANGLE_EXCLAMATION = 'TRIANGLE_EXCLAMATION',
  TRIANGLE_EXCLAMATION_EMPTY = 'TRIANGLE_EXCLAMATION_EMPTY',
  TRUCK_FIRE = 'TRUCK_FIRE',
  UNRATED_STAR = 'UNRATED_STAR',
  VERIFIED = 'VERIFIED',
  ABOUT_US = 'ABOUT_US',
}

/**
 * Icon map entry with component and theme colors.
 * default/filled use @loadable/component so webpack splits each icon into its own chunk.
 * Typed as `any` to work around duplicate @types/react from @types/react-redux.
 */
export interface IconMapEntry {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  default: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  filled?: any;
  /** Theme token (e.g., 'textPrimary'), direct color (e.g., '#000'), or 'currentColor' */
  defaultColor?: string;
  /** Color for filled variant (same format as defaultColor) */
  filledColor?: string;
}

/** Icon map for each store */
export type IconMap = {
  [key in IconName]?: IconMapEntry;
};

/** Store config from remote config */
export interface StoreConfig {
  storeName: string;
  storeId: string;
  isActive: boolean;
  url: string;
}

/**
 * Store icon configuration
 */
export interface StoreIcons {
  icons: IconMap;
}
